"use client";

import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-foreground/70 hover:text-primary hover:bg-transparent focus:bg-transparent uppercase text-[13px] font-mono tracking-[0.2em] px-3 py-2 transition-colors duration-300">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        <div className="h-12 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-12 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-12 w-48 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="group flex items-baseline gap-6 text-foreground hover:text-primary transition-colors duration-300">
          <span className="font-mono text-sm text-primary tracking-widest">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="font-default font-bold uppercase leading-none" style={{
        fontSize: "clamp(2.5rem, 8vw, 4rem)"
      }}>
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const heroThreshold = typeof window !== "undefined" ? window.innerHeight : 800;
    const handleScroll = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 20);
      if (currentY > heroThreshold) {
        if (currentY > lastScrollY && currentY > 100) {
          setHidden(true);
        } else if (currentY < lastScrollY) {
          setHidden(false);
        }
      } else {
        setHidden(false);
      }
      lastScrollY = currentY;
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="3088" id="header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-500 ease-out ${hidden ? "-translate-y-full" : "translate-y-0"} ${scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-transparent border-b border-transparent"}`} style={{
    backgroundColor: scrolled ? undefined : "transparent"
  }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="flex h-[clamp(4rem,12vw,5rem)] items-center justify-between gap-4 sm:gap-6">
          {}
          <div className="flex-shrink-0">
            <Link to="/" aria-label="The Garage Room home" className="inline-flex items-center">
              <WvcLogo className="h-7 text-primary !w-[274.391px] sm:h-[clamp(1.75rem,5vw,2rem)] [&_svg]:h-7 [&_svg]:text-primary [&_svg]:!w-[274.391px] [&_svg]:sm:h-[clamp(1.75rem,5vw,2rem)]" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex flex-shrink-0">
            <Button asChild variant="outline" size="sm" className="bg-primary border-none text-primary-foreground hover:bg-primary/90 hover:translate-x-1 hover:translate-y-1 hover:shadow-md rounded-md uppercase font-mono text-[12px] tracking-[0.16em] h-10 px-5 transition-all duration-0">
              <Link to="/BookPage">Book Session</Link>
            </Button>
          </div>

          {}
          <div className="lg:hidden flex items-center">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-primary/10 hover:text-primary rounded-md">
                  {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-border w-full sm:max-w-md p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between px-6 h-20 border-b border-border">
                    <Link to="/" onClick={() => setMobileOpen(false)}>
                      <WvcLogo className="h-6 text-primary !w-[245.594px] sm:h-[clamp(1.5rem,5vw,1.875rem)] [&_svg]:h-6 [&_svg]:text-primary [&_svg]:!w-[245.594px] [&_svg]:sm:h-[clamp(1.5rem,5vw,1.875rem)]" />
                    </Link>
                  </div>

                  <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-8 sm:py-12">
                    <MenuProvider menu_id="16">
                      <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                    </MenuProvider>
                  </div>

                  <div className="px-8 pb-10 pt-6 border-t border-border">
                    <Button asChild variant="outline" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border shadow-xs hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 px-4 py-2 has-[>svg]:px-3 w-full bg-primary border-none hover:bg-primary/90 uppercase font-mono text-[12px] tracking-[0.16em] h-10 sm:h-12 rounded-md transition-all duration-0 text-primary">
                      <Link to="/BookPage" onClick={() => setMobileOpen(false)}>
                        Book Session
                      </Link>
                    </Button>
                    <p className="mt-6 font-mono text-[11px] tracking-[0.18em] uppercase text-primary">
                      +44 20 7946 0958 // LIVE_SIGNAL
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}